import { useEffect, useMemo, useRef, useState } from 'react';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ImageIcon,
  Loader2,
  Minus,
  Phone,
  Plus,
  Search,
  ShoppingBag,
  Trash2,
  UserRound,
  UtensilsCrossed,
  X,
} from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

import {
  categoriesApi,
  menusApi,
  ordersApi,
  productsApi,
} from '../../api';
import { getApiErrorMessage } from '../../api/axiosClient';
import { useAlerts } from '../../components/feedback/AlertProvider';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import resolveMediaUrl from '../../utils/resolveMediaUrl';

import '../../styles/Orders/order-form.css';

const DEFAULT_ESTABLISHMENT_ID = 1;
const ESTABLISHMENT_STORAGE_KEY = 'selected_establishment_id';
const SELECTED_MENU_STORAGE_KEY = 'selected_menu_id';

const WAITER_ROLES = ['waiter', 'serveur'];

const normalizeRole = (role) =>
  String(role || '')
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, '');

const isWaiterRole = (user) =>
  WAITER_ROLES.includes(
    normalizeRole(user?.role || user?.type || user?.profile?.role),
  );

const translateFallback = (t, key, fallback) => {
  const value = t(key);
  return value === key ? fallback : value;
};

const toArray = (value) => {
  if (Array.isArray(value)) return value;
  if (Array.isArray(value?.data)) return value.data;
  if (value && typeof value === 'object') return Object.values(value);
  return [];
};

const normalizeTranslations = (translations) => {
  if (Array.isArray(translations)) return translations.filter(Boolean);

  if (translations && typeof translations === 'object') {
    return Object.entries(translations).map(([locale, translation]) => {
      if (!translation || typeof translation !== 'object') {
        return {
          locale,
          name: translation ? String(translation) : '',
        };
      }

      return {
        locale: translation.locale || locale,
        ...translation,
      };
    });
  }

  return [];
};

const getTranslatedField = (
  entity,
  field,
  language,
  fallbackLanguage = 'fr',
  fallbackValue = '',
) => {
  if (!entity) return fallbackValue;

  if (entity?.translations?.[language]?.[field]) {
    return entity.translations[language][field];
  }

  if (entity?.translations?.[fallbackLanguage]?.[field]) {
    return entity.translations[fallbackLanguage][field];
  }

  const translations = normalizeTranslations(entity.translations);

  const translation =
    translations.find((item) => item.locale === language) ||
    translations.find((item) => item.locale === fallbackLanguage) ||
    translations.find((item) => item.locale === 'en') ||
    translations.find((item) => item.locale === 'ar') ||
    translations.find((item) => item.locale === 'es') ||
    translations[0];

  return (
    translation?.[field] ||
    entity?.[`translated_${field}`] ||
    entity?.[field] ||
    fallbackValue
  );
};

const getProductSizes = (product) =>
  toArray(product?.sizes).filter((size) => size?.is_active !== false);

const getProductSupplements = (product) =>
  toArray(product?.supplements).filter(
    (supplement) => supplement?.is_active !== false,
  );

const getSupplementPrice = (supplement) =>
  Number(
    supplement?.price_override ??
      supplement?.price ??
      supplement?.extra_price ??
      0,
  );

const getPriceValue = (...values) => {
  const value = values.find(
    (item) => item !== null && item !== undefined && item !== '',
  );

  return Number(value || 0);
};

const getProductPrice = (product, sizeId = '') => {
  const sizes = getProductSizes(product);

  if (sizeId && sizes.length > 0) {
    const size = sizes.find(
      (item) => Number(item.id) === Number(sizeId),
    );

    if (size) {
      return getPriceValue(size.discount_price, size.price);
    }
  }

  return getPriceValue(
    product?.discount_price,
    product?.base_price,
    product?.price,
  );
};

const getProductCurrency = (product) =>
  product?.currency || product?.establishment?.currency || 'MAD';

const getProductImage = (product) => {
  const images = toArray(product?.images);

  const primaryImage =
    product?.primary_image ||
    images.find((image) => Boolean(image?.is_primary)) ||
    images[0];

  return resolveMediaUrl(
    primaryImage?.url ||
      primaryImage?.path ||
      product?.image_url ||
      product?.image,
  );
};

const getProductCategoryId = (product) =>
  product?.category_id ||
  product?.category?.id ||
  product?.menu_category_id ||
  '';

const deriveCategoriesFromProducts = (products) => {
  const map = new Map();

  products.forEach((product) => {
    const category = product?.category;
    const categoryId = getProductCategoryId(product);

    if (!category || !categoryId) return;

    if (!map.has(String(categoryId))) {
      map.set(String(categoryId), category);
    }
  });

  return Array.from(map.values());
};

function CustomSelect({
  value,
  options = [],
  onChange,
  placeholder,
  disabled = false,
}) {
  const ref = useRef(null);
  const [open, setOpen] = useState(false);

  const safeOptions = Array.isArray(options) ? options : [];

  const selectedOption = safeOptions.find(
    (option) => String(option.value) === String(value),
  );

  const selectedLabel =
    selectedOption?.label || placeholder || safeOptions[0]?.label || '—';

  useEffect(() => {
    if (!open) return undefined;

    const closeOnOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setOpen(false);
      }
    };

    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setOpen(false);
    };

    document.addEventListener('mousedown', closeOnOutside);
    document.addEventListener('keydown', closeOnEscape);

    return () => {
      document.removeEventListener('mousedown', closeOnOutside);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div
      ref={ref}
      className={`order-form-select ${
        open ? 'order-form-select--open' : ''
      } ${disabled ? 'order-form-select--disabled' : ''}`}
    >
      <button
        type="button"
        className="order-form-select__button"
        onClick={() => setOpen((current) => !current)}
        disabled={disabled}
      >
        <span>{selectedLabel}</span>
        <ChevronDown size={17} />
      </button>

      {open && (
        <div className="order-form-select__menu">
          {safeOptions.map((option) => {
            const active =
              String(option.value) === String(value);

            return (
              <button
                key={option.value || 'empty-option'}
                type="button"
                className={`order-form-select__option ${
                  active
                    ? 'order-form-select__option--active'
                    : ''
                }`}
                onClick={() => {
                  onChange(option.value);
                  setOpen(false);
                }}
              >
                <span>{option.label}</span>
                {active && <Check size={15} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function CustomerInfoModal({
  open,
  waiterRole,
  form,
  onChange,
  onContinue,
  t,
}) {
  const [validationMessage, setValidationMessage] = useState('');

  if (!open) return null;

  const typeOptions = waiterRole
    ? [
        {
          value: 'dine_in',
          label: t('orders.types.dine_in'),
        },
      ]
    : [
        {
          value: 'dine_in',
          label: t('orders.types.dine_in'),
        },
        {
          value: 'takeaway',
          label: t('orders.types.takeaway'),
        },
        {
          value: 'delivery',
          label: t('orders.types.delivery'),
        },
      ];

  const validate = () => {
    if (form.order_type === 'dine_in') {
      if (!form.table_number.trim()) {
        return translateFallback(
          t,
          'orders.form.validation.tableNumber',
          'Table number is required.',
        );
      }

      return '';
    }

    if (!form.customer_name.trim()) {
      return translateFallback(
        t,
        'orders.form.validation.customerName',
        'Customer name is required.',
      );
    }

    if (
      form.order_type === 'delivery' &&
      !form.customer_phone.trim()
    ) {
      return translateFallback(
        t,
        'orders.form.validation.customerPhone',
        'Customer phone is required for delivery.',
      );
    }

    return '';
  };

  const handleContinue = () => {
    const message = validate();

    if (message) {
      setValidationMessage(message);
      return;
    }

    setValidationMessage('');
    onContinue();
  };

  return (
    <div className="order-customer-modal">
      <div className="order-customer-modal__overlay" />

      <section className="order-customer-modal__content">
        <div className="order-customer-modal__icon">
          <UserRound size={28} />
        </div>

        <div className="order-customer-modal__heading">
          <p>
            {translateFallback(
              t,
              'orders.form.beforeOrdering',
              'Before starting the order',
            )}
          </p>

          <h2>
            {translateFallback(
              t,
              'orders.form.clientInformationTitle',
              'Order information',
            )}
          </h2>

          <span>
            {form.order_type === 'dine_in'
              ? translateFallback(
                  t,
                  'orders.form.dineInInformationHint',
                  'For dine-in orders, only the table number is required.',
                )
              : form.order_type === 'takeaway'
                ? translateFallback(
                    t,
                    'orders.form.takeawayInformationHint',
                    'For takeaway orders, the customer name is required.',
                  )
                : translateFallback(
                    t,
                    'orders.form.deliveryInformationHint',
                    'For delivery orders, customer name and phone are required.',
                  )}
          </span>
        </div>

        <div className="order-customer-modal__fields">
          <label className="order-customer-modal__field">
            <span>{t('orders.table.type')}</span>

            <CustomSelect
              value={form.order_type}
              options={typeOptions}
              disabled={waiterRole}
              onChange={(value) => {
                setValidationMessage('');
                onChange('order_type', value);
              }}
            />
          </label>

          {form.order_type === 'dine_in' ? (
            <label className="order-customer-modal__field">
              <span>{t('orders.table.tableNumber')}</span>

              <div className="order-customer-modal__input">
                <ShoppingBag size={17} />

                <input
                  autoFocus
                  value={form.table_number}
                  placeholder={t(
                    'orders.form.tableNumberPlaceholder',
                  )}
                  onChange={(event) => {
                    setValidationMessage('');
                    onChange(
                      'table_number',
                      event.target.value,
                    );
                  }}
                />
              </div>
            </label>
          ) : (
            <>
              <label className="order-customer-modal__field">
                <span>{t('orders.table.customer')}</span>

                <div className="order-customer-modal__input">
                  <UserRound size={17} />

                  <input
                    autoFocus
                    value={form.customer_name}
                    placeholder={t(
                      'orders.form.customerNamePlaceholder',
                    )}
                    onChange={(event) => {
                      setValidationMessage('');
                      onChange(
                        'customer_name',
                        event.target.value,
                      );
                    }}
                  />
                </div>
              </label>

              <label className="order-customer-modal__field">
                <span>
                  {t('orders.table.phone')}
                  {form.order_type === 'takeaway'
                    ? ` (${translateFallback(
                        t,
                        'common.optional',
                        'optional',
                      )})`
                    : ''}
                </span>

                <div className="order-customer-modal__input">
                  <Phone size={17} />

                  <input
                    value={form.customer_phone}
                    placeholder={t(
                      'orders.form.customerPhonePlaceholder',
                    )}
                    onChange={(event) => {
                      setValidationMessage('');
                      onChange(
                        'customer_phone',
                        event.target.value,
                      );
                    }}
                  />
                </div>
              </label>
            </>
          )}
        </div>

        {validationMessage && (
          <div className="order-customer-modal__validation">
            {validationMessage}
          </div>
        )}

        <button
          type="button"
          className="order-customer-modal__continue"
          onClick={handleContinue}
        >
          <UtensilsCrossed size={18} />

          {translateFallback(
            t,
            'orders.form.showMenu',
            'Show menu',
          )}
        </button>
      </section>
    </div>
  );
}

function ProductOptionsModal({
  product,
  language,
  t,
  onClose,
  onAdd,
}) {
  const sizes = getProductSizes(product);
  const supplements = getProductSupplements(product);

  const defaultSize =
    sizes.find((size) => Boolean(size.is_default)) ||
    sizes[0] ||
    null;

  const [selectedSizeId, setSelectedSizeId] = useState(
    defaultSize?.id ? String(defaultSize.id) : '',
  );

  const [selectedSupplementIds, setSelectedSupplementIds] =
    useState([]);

  const [quantity, setQuantity] = useState(1);
  const [note, setNote] = useState('');

  const productName = getTranslatedField(
    product,
    'name',
    language,
    'fr',
    translateFallback(t, 'products.noName', 'Product'),
  );

  const description = getTranslatedField(
    product,
    'description',
    language,
    'fr',
    '',
  );

  const imageUrl = getProductImage(product);
  const currency = getProductCurrency(product);

  const basePrice = getProductPrice(
    product,
    selectedSizeId,
  );

  const selectedSupplements = supplements.filter((supplement) =>
    selectedSupplementIds.includes(String(supplement.id)),
  );

  const supplementsPrice = selectedSupplements.reduce(
    (total, supplement) =>
      total + getSupplementPrice(supplement),
    0,
  );

  const unitPrice = basePrice + supplementsPrice;
  const totalPrice = unitPrice * quantity;

  const sizeOptions = sizes.map((size) => ({
    value: String(size.id),
    label: `${getTranslatedField(
      size,
      'name',
      language,
      'fr',
      translateFallback(t, 'products.form.size', 'Size'),
    )} · ${getPriceValue(
      size.discount_price,
      size.price,
    ).toFixed(2)} ${currency}`,
  }));

  const toggleSupplement = (supplementId) => {
    const id = String(supplementId);

    setSelectedSupplementIds((currentIds) =>
      currentIds.includes(id)
        ? currentIds.filter((currentId) => currentId !== id)
        : [...currentIds, id],
    );
  };

  return (
    <div className="order-product-modal">
      <button
        type="button"
        className="order-product-modal__overlay"
        onClick={onClose}
        aria-label={t('common.close')}
      />

      <section className="order-product-modal__content">
        <button
          type="button"
          className="order-product-modal__close"
          onClick={onClose}
          aria-label={t('common.close')}
        >
          <X size={20} />
        </button>

        <div className="order-product-modal__product">
          <div className="order-product-modal__image">
            {imageUrl ? (
              <img src={imageUrl} alt={productName} />
            ) : (
              <ImageIcon size={38} />
            )}
          </div>

          <div>
            <h2>{productName}</h2>

            {description && <p>{description}</p>}

            <strong>
              {unitPrice.toFixed(2)} {currency}
            </strong>
          </div>
        </div>

        <div className="order-product-modal__body">
          {sizeOptions.length > 0 && (
            <section className="order-product-modal__option-block">
              <div className="order-product-modal__option-title">
                <span>
                  {translateFallback(
                    t,
                    'orders.form.size',
                    'Size',
                  )}
                </span>

                <small>
                  {translateFallback(
                    t,
                    'orders.form.chooseSize',
                    'Choose a size',
                  )}
                </small>
              </div>

              <CustomSelect
                value={selectedSizeId}
                options={sizeOptions}
                onChange={setSelectedSizeId}
              />
            </section>
          )}

          {supplements.length > 0 && (
            <section className="order-product-modal__option-block">
              <div className="order-product-modal__option-title">
                <span>
                  {translateFallback(
                    t,
                    'orders.form.addons',
                    'Add-ons',
                  )}
                </span>

                <small>
                  {translateFallback(
                    t,
                    'orders.form.chooseAddons',
                    'Choose any extras for this dish',
                  )}
                </small>
              </div>

              <div className="order-product-modal__addons">
                {supplements.map((supplement) => {
                  const supplementId = String(supplement.id);
                  const active =
                    selectedSupplementIds.includes(supplementId);

                  const supplementName = getTranslatedField(
                    supplement,
                    'name',
                    language,
                    'fr',
                    translateFallback(
                      t,
                      'orders.form.addon',
                      'Add-on',
                    ),
                  );

                  const price = getSupplementPrice(supplement);

                  return (
                    <button
                      key={supplement.id}
                      type="button"
                      className={`order-product-modal__addon ${
                        active ? 'is-active' : ''
                      }`}
                      onClick={() =>
                        toggleSupplement(supplement.id)
                      }
                    >
                      <span className="order-product-modal__addon-check">
                        {active ? (
                          <Check size={15} />
                        ) : (
                          <Plus size={15} />
                        )}
                      </span>

                      <span className="order-product-modal__addon-content">
                        <strong>{supplementName}</strong>

                        <small>
                          + {price.toFixed(2)} {currency}
                        </small>
                      </span>
                    </button>
                  );
                })}
              </div>
            </section>
          )}

          <section className="order-product-modal__option-block">
            <div className="order-product-modal__quantity-row">
              <div className="order-product-modal__option-title">
                <span>
                  {translateFallback(
                    t,
                    'orders.form.quantity',
                    'Quantity',
                  )}
                </span>

                <small>
                  {translateFallback(
                    t,
                    'orders.form.quantityDescription',
                    'How many of this item?',
                  )}
                </small>
              </div>

              <div className="order-form-quantity">
                <button
                  type="button"
                  onClick={() =>
                    setQuantity((current) =>
                      Math.max(1, current - 1),
                    )
                  }
                >
                  <Minus size={16} />
                </button>

                <strong>{quantity}</strong>

                <button
                  type="button"
                  onClick={() =>
                    setQuantity((current) => current + 1)
                  }
                >
                  <Plus size={16} />
                </button>
              </div>
            </div>
          </section>

          <section className="order-product-modal__option-block">
            <label className="order-product-modal__field">
              <div className="order-product-modal__option-title">
                <span>
                  {translateFallback(
                    t,
                    'orders.form.itemNote',
                    'Note',
                  )}
                </span>

                <small>
                  {translateFallback(
                    t,
                    'orders.form.noteDescription',
                    'Special instructions for this dish',
                  )}
                </small>
              </div>

              <textarea
                value={note}
                placeholder={t(
                  'orders.form.itemNotePlaceholder',
                )}
                onChange={(event) =>
                  setNote(event.target.value)
                }
              />
            </label>
          </section>
        </div>

        <div className="order-product-modal__footer">
          <div>
            <span>{t('orders.details.total')}</span>
            <strong>
              {totalPrice.toFixed(2)} {currency}
            </strong>
          </div>

          <button
            type="button"
            className="order-product-modal__add"
            onClick={() =>
              onAdd({
                product,
                product_id: product.id,
                product_size_id: selectedSizeId || null,
                supplement_ids: selectedSupplements.map(
                  (supplement) => supplement.id,
                ),
                supplements: selectedSupplements,
                quantity,
                unit_price: unitPrice,
                total_price: totalPrice,
                note: note.trim() || '',
              })
            }
          >
            <Plus size={18} />

            {translateFallback(
              t,
              'orders.form.addToOrder',
              'Add to order',
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

function OrderSummaryModal({
  items,
  language,
  total,
  saving,
  t,
  onClose,
  onRemove,
  onConfirm,
}) {
  return (
    <div className="order-summary-modal">
      <button
        type="button"
        className="order-summary-modal__overlay"
        onClick={onClose}
        aria-label={t('common.close')}
      />

      <section className="order-summary-modal__content">
        <div className="order-summary-modal__head">
          <div>
            <p>
              {translateFallback(
                t,
                'orders.form.orderSummary',
                'Order summary',
              )}
            </p>

            <h2>
              {translateFallback(
                t,
                'orders.form.reviewOrder',
                'Review the order',
              )}
            </h2>
          </div>

          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X size={20} />
          </button>
        </div>

        <div className="order-summary-modal__items">
          {items.map((item) => {
            const imageUrl = getProductImage(item.product);

            return (
              <article
                key={item.row_id}
                className="order-summary-item"
              >
                <div className="order-summary-item__image">
                  {imageUrl ? (
                    <img
                      src={imageUrl}
                      alt={getTranslatedField(
                        item.product,
                        'name',
                        language,
                        'fr',
                        '',
                      )}
                    />
                  ) : (
                    <ImageIcon size={20} />
                  )}
                </div>

                <div className="order-summary-item__content">
                  <strong>
                    {getTranslatedField(
                      item.product,
                      'name',
                      language,
                      'fr',
                      translateFallback(
                        t,
                        'products.noName',
                        'Product',
                      ),
                    )}
                  </strong>

                  <span>
                    {item.quantity} ×{' '}
                    {item.unit_price.toFixed(2)}{' '}
                    {getProductCurrency(item.product)}
                  </span>

                  {Array.isArray(item.supplements) &&
                    item.supplements.length > 0 && (
                      <small>
                        {item.supplements
                          .map((supplement) =>
                            getTranslatedField(
                              supplement,
                              'name',
                              language,
                              'fr',
                              '',
                            ),
                          )
                          .filter(Boolean)
                          .join(', ')}
                      </small>
                    )}

                  {item.note && <small>{item.note}</small>}
                </div>

                <button
                  type="button"
                  className="order-summary-item__delete"
                  onClick={() => onRemove(item.row_id)}
                >
                  <Trash2 size={16} />
                </button>
              </article>
            );
          })}
        </div>

        <div className="order-summary-modal__footer">
          <div>
            <span>{t('orders.details.total')}</span>

            <strong>{total.toFixed(2)} MAD</strong>
          </div>

          <button
            type="button"
            className="order-summary-modal__confirm"
            onClick={onConfirm}
            disabled={saving || items.length === 0}
          >
            {saving ? (
              <Loader2 size={18} />
            ) : (
              <Check size={18} />
            )}

            {translateFallback(
              t,
              'orders.form.confirmOrder',
              'Confirm order',
            )}
          </button>
        </div>
      </section>
    </div>
  );
}

function OrderForm() {
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { showAlert } = useAlerts();

  const waiterRole = isWaiterRole(user);

  const establishmentId =
    localStorage.getItem(ESTABLISHMENT_STORAGE_KEY) ||
    DEFAULT_ESTABLISHMENT_ID;

  const [menus, setMenus] = useState([]);
  const [menusLoading, setMenusLoading] = useState(true);

  const [selectedMenuId, setSelectedMenuId] = useState(
    localStorage.getItem(SELECTED_MENU_STORAGE_KEY) || '',
  );

  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);

  const [selectedCategoryId, setSelectedCategoryId] =
    useState('');

  const categorySliderRef = useRef(null);

  const [selectedProduct, setSelectedProduct] = useState(null);

  const [cartItems, setCartItems] = useState([]);
  const [showSummary, setShowSummary] = useState(false);

  const [search, setSearch] = useState('');

  const [loadingProducts, setLoadingProducts] = useState(true);
  const [saving, setSaving] = useState(false);

  const [customerInfoReady, setCustomerInfoReady] = useState(false);

  const [customerForm, setCustomerForm] = useState({
    customer_name: '',
    customer_phone: '',
    order_type: waiterRole ? 'dine_in' : 'dine_in',
    table_number: '',
  });

  const selectedMenu = useMemo(
    () =>
      menus.find(
        (menu) => String(menu.id) === String(selectedMenuId),
      ) || null,
    [menus, selectedMenuId],
  );

  const menuOptions = useMemo(
    () =>
      menus.map((menu) => ({
        value: String(menu.id),
        label: `${menu.name}${
          menu.is_default ? ` • ${t('menus.status.default')}` : ''
        }`,
      })),
    [menus, t],
  );

  const visibleCategories = useMemo(
    () =>
      categories.filter(
        (category) =>
          category?.is_active !== false &&
          category?.is_active !== 0,
      ),
    [categories],
  );

  const filteredProducts = useMemo(() => {
    const keyword = search.trim().toLowerCase();

    return products.filter((product) => {
      const productName = getTranslatedField(
        product,
        'name',
        language,
        'fr',
        '',
      ).toLowerCase();

      const matchesSearch =
        !keyword || productName.includes(keyword);

      const matchesCategory =
        !selectedCategoryId ||
        String(getProductCategoryId(product)) ===
          String(selectedCategoryId);

      return matchesSearch && matchesCategory;
    });
  }, [
    products,
    search,
    selectedCategoryId,
    language,
  ]);

  const productsByCategory = useMemo(() => {
    if (selectedCategoryId || search.trim()) {
      return [
        {
          category: visibleCategories.find(
            (category) =>
              String(category.id) ===
              String(selectedCategoryId),
          ),
          products: filteredProducts,
        },
      ];
    }

    const sections = visibleCategories
      .map((category) => ({
        category,
        products: products.filter(
          (product) =>
            String(getProductCategoryId(product)) ===
            String(category.id),
        ),
      }))
      .filter((section) => section.products.length > 0);

    const categoryIds = visibleCategories.map(
      (category) => String(category.id),
    );

    const uncategorizedProducts = products.filter(
      (product) =>
        !categoryIds.includes(
          String(getProductCategoryId(product)),
        ),
    );

    if (uncategorizedProducts.length > 0) {
      sections.push({
        category: null,
        products: uncategorizedProducts,
      });
    }

    return sections;
  }, [
    products,
    filteredProducts,
    visibleCategories,
    selectedCategoryId,
    search,
  ]);

  const cartCount = useMemo(
    () =>
      cartItems.reduce(
        (total, item) =>
          total + Number(item.quantity || 0),
        0,
      ),
    [cartItems],
  );

  const totalAmount = useMemo(
    () =>
      cartItems.reduce(
        (total, item) =>
          total + Number(item.total_price || 0),
        0,
      ),
    [cartItems],
  );

  const fetchMenus = async () => {
    try {
      setMenusLoading(true);

      const response =
        await menusApi.getMenus(establishmentId);

      const responseMenus =
        response.data?.data?.menus ||
        response.data?.data ||
        [];

      const normalizedMenus = Array.isArray(responseMenus)
        ? responseMenus
        : [];

      setMenus(normalizedMenus);

      const storedMenuId = localStorage.getItem(
        SELECTED_MENU_STORAGE_KEY,
      );

      const storedMenuExists = normalizedMenus.some(
        (menu) =>
          String(menu.id) === String(storedMenuId),
      );

      if (storedMenuExists) {
        setSelectedMenuId(String(storedMenuId));
        return;
      }

      const defaultMenu =
        normalizedMenus.find((menu) =>
          Boolean(menu.is_default),
        ) ||
        normalizedMenus.find((menu) =>
          Boolean(menu.is_active),
        ) ||
        normalizedMenus[0];

      if (defaultMenu?.id) {
        const nextMenuId = String(defaultMenu.id);

        localStorage.setItem(
          SELECTED_MENU_STORAGE_KEY,
          nextMenuId,
        );

        setSelectedMenuId(nextMenuId);
      } else {
        localStorage.removeItem(
          SELECTED_MENU_STORAGE_KEY,
        );

        setSelectedMenuId('');
      }
    } catch (error) {
      setMenus([]);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setMenusLoading(false);
    }
  };

  const fetchMenuContent = async () => {
    if (!selectedMenuId) {
      setCategories([]);
      setProducts([]);
      setLoadingProducts(false);
      return;
    }

    try {
      setLoadingProducts(true);

      const [productsResult, categoriesResult] =
        await Promise.allSettled([
          productsApi.getProducts(establishmentId, {
            menu_id: selectedMenuId,
            per_page: 100,
          }),
          categoriesApi.getCategoryTree(establishmentId, {
            menu_id: selectedMenuId,
            per_page: 100,
          }),
        ]);

      if (productsResult.status === 'rejected') {
        throw productsResult.reason;
      }

      const nextProducts = toArray(
        productsResult.value.data?.data,
      );

      setProducts(nextProducts);

      if (categoriesResult.status === 'fulfilled') {
        setCategories(
          toArray(categoriesResult.value.data?.data),
        );
      } else {
        setCategories(
          deriveCategoriesFromProducts(nextProducts),
        );
      }
    } catch (error) {
      setProducts([]);
      setCategories([]);

      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setLoadingProducts(false);
    }
  };

  useEffect(() => {
    fetchMenus();
  }, [establishmentId]);

  useEffect(() => {
    setSelectedCategoryId('');
    setSearch('');
    setCartItems([]);
    fetchMenuContent();
  }, [selectedMenuId]);

  useEffect(() => {
    if (!waiterRole) return;

    setCustomerForm((currentForm) => ({
      ...currentForm,
      order_type: 'dine_in',
    }));
  }, [waiterRole]);

  const updateCustomerForm = (key, value) => {
    setCustomerForm((currentForm) => ({
      ...currentForm,
      [key]: value,
    }));
  };

  const scrollCategories = (direction) => {
    const slider = categorySliderRef.current;

    if (!slider) return;

    const distance = Math.max(
      260,
      Math.round(slider.clientWidth * 0.72),
    );

    slider.scrollBy({
      left: direction * distance,
      behavior: 'smooth',
    });
  };

  const handleMenuChange = (menuId) => {
    setSelectedMenuId(menuId);

    if (menuId) {
      localStorage.setItem(
        SELECTED_MENU_STORAGE_KEY,
        menuId,
      );
    } else {
      localStorage.removeItem(
        SELECTED_MENU_STORAGE_KEY,
      );
    }
  };

  const addProductToOrder = (item) => {
    setCartItems((currentItems) => [
      ...currentItems,
      {
        ...item,
        row_id: `${item.product_id}-${Date.now()}-${Math.random()}`,
      },
    ]);

    setSelectedProduct(null);
  };

  const handleProductClick = (product) => {
    setSelectedProduct(product);
  };

  const removeCartItem = (rowId) => {
    setCartItems((currentItems) =>
      currentItems.filter(
        (item) => item.row_id !== rowId,
      ),
    );
  };

  const validateOrder = () => {
    if (!selectedMenuId) {
      return translateFallback(
        t,
        'orders.form.validation.menu',
        'Please select a menu.',
      );
    }

    if (!cartItems.length) {
      return translateFallback(
        t,
        'orders.form.validation.items',
        'Please add at least one product.',
      );
    }

    if (customerForm.order_type === 'dine_in') {
      if (!customerForm.table_number.trim()) {
        return translateFallback(
          t,
          'orders.form.validation.tableNumber',
          'Table number is required.',
        );
      }

      return '';
    }

    if (!customerForm.customer_name.trim()) {
      return translateFallback(
        t,
        'orders.form.validation.customerName',
        'Customer name is required.',
      );
    }

    if (
      customerForm.order_type === 'delivery' &&
      !customerForm.customer_phone.trim()
    ) {
      return translateFallback(
        t,
        'orders.form.validation.customerPhone',
        'Customer phone is required for delivery.',
      );
    }

    return '';
  };

  const submitOrder = async () => {
    const validationMessage = validateOrder();

    if (validationMessage) {
      showAlert({
        type: 'warning',
        title: t('alerts.warning'),
        message: validationMessage,
      });

      return;
    }

    try {
      setSaving(true);

      const payload = {
        menu_id: selectedMenuId,
        customer_name:
          customerForm.order_type === 'dine_in'
            ? `${translateFallback(
                t,
                'orders.form.tableCustomer',
                'Table',
              )} ${customerForm.table_number.trim()}`
            : customerForm.customer_name.trim(),
        customer_phone:
          customerForm.order_type === 'dine_in'
            ? '-'
            : customerForm.customer_phone.trim() || '-',
        customer_note: null,
        order_type: customerForm.order_type,
        table_number:
          customerForm.order_type === 'dine_in'
            ? customerForm.table_number.trim()
            : null,
        status: waiterRole ? 'accepted' : undefined,
        items: cartItems.map((item) => ({
          product_id: item.product_id,
          product_size_id:
            item.product_size_id || null,
          supplement_ids: item.supplement_ids || [],
          quantity: item.quantity,
          note: item.note || null,
        })),
      };

      const response =
        await ordersApi.createOrder(
          establishmentId,
          payload,
        );

      const createdOrder = response.data?.data;

      showAlert({
        type: 'success',
        title: t('alerts.success'),
        message: t('orders.form.alerts.created'),
      });

      if (waiterRole) {
        navigate('/orders');
      } else if (createdOrder?.id) {
        navigate(`/orders/${createdOrder.id}`);
      } else {
        navigate('/orders');
      }
    } catch (error) {
      showAlert({
        type: 'error',
        title: t('alerts.error'),
        message: getApiErrorMessage(error),
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main
      className={`order-menu-page ${
        language === 'ar'
          ? 'order-menu-page--rtl'
          : 'order-menu-page--ltr'
      }`}
    >
      <header className="order-menu-header">
        <Link to="/orders" className="order-menu-back">
          <ArrowLeft size={18} />
          <span>{t('orders.details.backToOrders')}</span>
        </Link>

        <div className="order-menu-header__copy">
          <p className="order-menu-eyebrow">
            {t('orders.form.eyebrow')}
          </p>

          <h1>
            {selectedMenu?.name ||
              translateFallback(
                t,
                'orders.form.menuTitle',
                'Restaurant menu',
              )}
          </h1>

          <span>
            {waiterRole
              ? t('orders.form.waiterDescription')
              : t('orders.form.description')}
          </span>
        </div>
      </header>

      <section className="order-menu-card order-menu-card--catalog">
        <div className="order-menu-card__title">
          <span className="order-menu-card__icon">
            <UtensilsCrossed size={22} />
          </span>

          <div>
            <h2>{t('orders.form.productsTitle')}</h2>
            <p>{t('orders.form.productsDescription')}</p>
          </div>
        </div>

        <div className="order-menu-header__actions">
          <button
            type="button"
            className="order-menu-customer-button"
            onClick={() => setCustomerInfoReady(false)}
          >
            <UserRound size={17} />

            <span>
              {customerForm.order_type === 'dine_in'
                ? `${t('orders.table.tableNumber')}: ${
                    customerForm.table_number || '—'
                  }`
                : customerForm.customer_name || '—'}
            </span>
          </button>

          <CustomSelect
            value={selectedMenuId}
            options={menuOptions}
            placeholder={
              menusLoading
                ? t('menus.loading')
                : t('menus.empty.title')
            }
            disabled={
              menusLoading || menus.length === 0
            }
            onChange={handleMenuChange}
          />

          <label className="order-menu-search">
            <Search size={18} />

            <input
              type="search"
              value={search}
              placeholder={t(
                'orders.form.searchProducts',
              )}
              disabled={!selectedMenuId}
              onChange={(event) =>
                setSearch(event.target.value)
              }
            />
          </label>
        </div>

      {visibleCategories.length > 0 && (
        <div className="order-menu-categories-slider">
          <button
            type="button"
            className="order-menu-categories-slider__arrow"
            onClick={() => scrollCategories(-1)}
            aria-label={translateFallback(
              t,
              'common.previous',
              'Previous categories',
            )}
          >
            <ChevronLeft size={20} />
          </button>

          <nav
            ref={categorySliderRef}
            className="order-menu-categories"
          >
            <button
              type="button"
              className={
                !selectedCategoryId ? 'is-active' : ''
              }
              onClick={() =>
                setSelectedCategoryId('')
              }
            >
              {translateFallback(
                t,
                'common.all',
                'All',
              )}
            </button>

            {visibleCategories.map((category) => (
              <button
                key={category.id}
                type="button"
                className={
                  String(selectedCategoryId) ===
                  String(category.id)
                    ? 'is-active'
                    : ''
                }
                onClick={() =>
                  setSelectedCategoryId(
                    String(category.id),
                  )
                }
              >
                {getTranslatedField(
                  category,
                  'name',
                  language,
                  'fr',
                  translateFallback(
                    t,
                    'categories.noName',
                    'Category',
                  ),
                )}
              </button>
            ))}
          </nav>

          <button
            type="button"
            className="order-menu-categories-slider__arrow"
            onClick={() => scrollCategories(1)}
            aria-label={translateFallback(
              t,
              'common.next',
              'Next categories',
            )}
          >
            <ChevronRight size={20} />
          </button>
        </div>
      )}

      <section className="order-menu-content">
        {!selectedMenuId && !menusLoading ? (
          <div className="order-menu-state">
            <UtensilsCrossed size={34} />
            <p>
              {translateFallback(
                t,
                'orders.form.selectMenuFirst',
                'Select a menu to start.',
              )}
            </p>
          </div>
        ) : loadingProducts ? (
          <div className="order-menu-state">
            <Loader2
              size={34}
              className="order-menu-loader"
            />
            <p>
              {t('orders.form.loadingProducts')}
            </p>
          </div>
        ) : productsByCategory.length === 0 ? (
          <div className="order-menu-state">
            <UtensilsCrossed size={34} />
            <p>{t('orders.form.noProducts')}</p>
          </div>
        ) : (
          productsByCategory.map(
            ({ category, products: sectionProducts }, index) => (
              <section
                key={category?.id || `section-${index}`}
                className="order-menu-section"
              >
                <div className="order-menu-section__heading">
                  <div>
                    <p>
                      {translateFallback(
                        t,
                        'orders.form.category',
                        'Category',
                      )}
                    </p>

                    <h2>
                      {category
                        ? getTranslatedField(
                            category,
                            'name',
                            language,
                            'fr',
                            translateFallback(
                              t,
                              'categories.noName',
                              'Category',
                            ),
                          )
                        : translateFallback(
                            t,
                            'orders.form.otherProducts',
                            'Other products',
                          )}
                    </h2>

                    {category &&
                      getTranslatedField(
                        category,
                        'description',
                        language,
                        'fr',
                        '',
                      ) && (
                        <span>
                          {getTranslatedField(
                            category,
                            'description',
                            language,
                            'fr',
                            '',
                          )}
                        </span>
                      )}
                  </div>

                  <strong>{sectionProducts.length}</strong>
                </div>

                <div className="order-menu-products">
                  {sectionProducts.map((product) => {
                    const imageUrl =
                      getProductImage(product);

                    const productName =
                      getTranslatedField(
                        product,
                        'name',
                        language,
                        'fr',
                        translateFallback(
                          t,
                          'products.noName',
                          'Product',
                        ),
                      );

                    const description =
                      getTranslatedField(
                        product,
                        'description',
                        language,
                        'fr',
                        '',
                      );

                    const currency =
                      getProductCurrency(product);

                    return (
                      <button
                        key={product.id}
                        type="button"
                        className="order-menu-product-card"
                        onClick={() =>
                          handleProductClick(product)
                        }
                      >
                        <span className="order-menu-product-card__image">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={productName}
                            />
                          ) : (
                            <ImageIcon size={34} />
                          )}

                          <span className="order-menu-product-card__plus">
                            <Plus size={18} />
                          </span>
                        </span>

                        <span className="order-menu-product-card__content">
                          <strong>{productName}</strong>

                          {description && (
                            <small>{description}</small>
                          )}

                          <span>
                            {getProductPrice(
                              product,
                            ).toFixed(2)}{' '}
                            {currency}
                          </span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </section>
            ),
          )
        )}
      </section>
      </section>

      {cartItems.length > 0 && (
        <aside className="order-menu-bottom-bar">
          <button
            type="button"
            className="order-menu-bottom-bar__summary"
            onClick={() => setShowSummary(true)}
          >
            <span className="order-menu-bottom-bar__icon">
              <ShoppingBag size={20} />
              <strong>{cartCount}</strong>
            </span>

            <span>
              <small>
                {translateFallback(
                  t,
                  'orders.form.cartTitle',
                  'Current order',
                )}
              </small>

              <strong>
                {totalAmount.toFixed(2)} MAD
              </strong>
            </span>
          </button>

          <button
            type="button"
            className="order-menu-bottom-bar__confirm"
            onClick={() => setShowSummary(true)}
          >
            <Check size={18} />

            {translateFallback(
              t,
              'orders.form.confirmOrder',
              'Confirm order',
            )}
          </button>
        </aside>
      )}

      <CustomerInfoModal
        open={!customerInfoReady}
        waiterRole={waiterRole}
        form={customerForm}
        onChange={updateCustomerForm}
        onContinue={() => setCustomerInfoReady(true)}
        t={t}
      />

      {selectedProduct && (
        <ProductOptionsModal
          product={selectedProduct}
          language={language}
          t={t}
          onClose={() => setSelectedProduct(null)}
          onAdd={addProductToOrder}
        />
      )}

      {showSummary && (
        <OrderSummaryModal
          items={cartItems}
          language={language}
          total={totalAmount}
          saving={saving}
          t={t}
          onClose={() => setShowSummary(false)}
          onRemove={removeCartItem}
          onConfirm={submitOrder}
        />
      )}
    </main>
  );
}

export default OrderForm;